import { ref, computed } from "vue";
import type { ISatMapProps } from "@chatfood/design-system";
import type { IDomainOutletCurbside } from "~/domain/outlet-curbside";
import type { IDomainCoordinate } from "~/domain/coordinate";
import { useBusinessStore } from "~/stores/business";
import { useNavigation } from "~/composables/use-navigation";
import { useDirectBackend } from "#imports";

export const useCurbside = () => {
  const business = useBusinessStore();
  const { push } = useNavigation();
  const { storeRepo, outletRepo } = useDirectBackend();

  const isLoading = ref(false);
  const locationsSource = ref<IDomainOutletCurbside[]>([]);
  const clickedLocation = ref<IDomainOutletCurbside>();
  const focusedLocation = ref<IDomainOutletCurbside>();
  const currentGeoLocation = ref<IDomainCoordinate>();

  const mapBoundaries = ref<ISatMapProps["mapBounds"]>({
    location: business.country,
  });

  const locations = computed(() => {
    return locationsSource.value.map((location) => ({
      ...location,
      isSelected: clickedLocation.value?.id === location.id,
      isFocused: focusedLocation.value?.id === location.id,
    }));
  });

  const updateMapBoundaries = () => {
    const allCoordinates = [
      ...(currentGeoLocation.value ? [currentGeoLocation.value] : []),
      ...locations.value
        .map((location) => location.coordinates)
        .filter(
          (coordinates): coordinates is IDomainCoordinate =>
            coordinates !== undefined,
        ),
    ];

    const lats = allCoordinates.map((coord) => coord.lat);
    const lngs = allCoordinates.map((coord) => coord.lng);

    const bounds = {
      north: Math.max(...lats),
      south: Math.min(...lats),
      east: Math.max(...lngs),
      west: Math.min(...lngs),
    };

    mapBoundaries.value = {
      location: business.country,
      bounds,
    };
  };

  const onLocationsLoaded = (data: IDomainOutletCurbside[]) => {
    locationsSource.value = data;
    updateMapBoundaries();
  };

  const onLocationClicked = (location: IDomainOutletCurbside) => {
    clickedLocation.value = location;

    if (!location.coordinates) return;

    mapBoundaries.value = {
      location: business.country,
      coordinates: {
        lat: location.coordinates.lat,
        lng: location.coordinates.lng,
      },
      bounds: undefined,
    };
  };

  const onLocationFocused = (location: IDomainOutletCurbside) => {
    focusedLocation.value = location;
  };

  const onLocationBlur = () => {
    focusedLocation.value = undefined;
  };

  const onCurbsideAt = async (storeId: string) => {
    isLoading.value = true;

    try {
      const store = await storeRepo.getStore({
        accountId: business.accountId,
        channelLinkId: storeId,
      });

      const outlet = await outletRepo.getOutletById({
        outletId: store.outletId,
        salesChannel: business.salesChannel,
        orderingMode: "curbside",
      });

      await push("outlet", {
        orderingMode: "curbside",
        outletSlug: outlet.slug,
      });
    } catch {
      isLoading.value = false;
    }
  };

  const setCurrentGeoLocation = (coordinate?: IDomainCoordinate) => {
    currentGeoLocation.value = coordinate;
    updateMapBoundaries();
  };

  const resolveOutletId = async (
    storeId: string,
  ): Promise<string | undefined> => {
    try {
      const store = await storeRepo.getStore({
        accountId: business.accountId,
        channelLinkId: storeId,
      });

      return store.outletId;
    } catch {
      return undefined;
    }
  };

  return {
    mapBoundaries,
    isLoading,
    clickedLocation,
    focusedLocation,
    locations,
    currentGeoLocation,
    onLocationsLoaded,
    onLocationClicked,
    onLocationBlur,
    onLocationFocused,
    updateMapBoundaries,
    onCurbsideAt,
    setCurrentGeoLocation,
    resolveOutletId,
  };
};
